import { BLEND_MODES, Graphics, Rectangle, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { COLORS, INTRO_FONT, METRICS } from "./intro_theme"
import { Tween, tween } from "./intro_motion"
import { lerpColor } from "./logo_choreography"

export type ButtonVariant = "primary" | "ghost"

interface LoginButtonProps {
    label: string
    width: number
    variant: ButtonVariant
    onClick: () => void
    height?: number
}

/** How long hover takes to light a button up, and to let it go. */
const HOVER_MS = 140
/** The sheen's crossing, and how wide its band of light is. */
const SHEEN_MS = 820
const SHEEN_W = 46

/**
 * A real button for the login card: fixed size, a label centred in it, and hover/press states.
 *
 * The intro used to render bare Text ("Login", "Submit", "Cancel") with a click handler on the
 * surrounding container -- no bounds, no hover, no cursor, and a click target that ran the width of
 * the line. This draws an actual control instead.
 *
 * States EASE rather than snap: hover lights the button up over a beat and lets it go over the same,
 * so moving across the card reads as the controls responding rather than flickering between two
 * pictures. The primary carries a soft halo that blooms with the hover -- the one warm thing on the
 * card that says "this is the way in".
 */
export class LoginButton extends Graphics {
    private label: Text
    private variant: ButtonVariant
    private w: number
    private h: number
    private hover = 0
    private hovered = false
    private pressed = false
    private enabled = true
    private hoverTween?: Tween

    private sheen = new Graphics()
    private sheenMask = new Graphics()
    private sheenTween?: Tween

    constructor({ label, width, variant, onClick, height }: LoginButtonProps) {
        super()

        this.variant = variant
        this.w = width
        this.h = height ?? METRICS.buttonHeight

        this.label = new TextRenderer()
            .fontFamily(INTRO_FONT)
            .role("body").bold(false)
            .color(variant === "primary" ? COLORS.onAccent : COLORS.text)
            .align("center")
            .render(label) as Text
        if (variant === "primary") {
            this.label.style.fontWeight = "600"
        } else {
            this.label.style.fontWeight = "500"
        }
        this.addChild(this.label)

        // The sheen is clipped to the button's own shape and added onto it, so it brightens the fill
        // as it crosses rather than laying a grey bar over it.
        this.sheenMask.beginFill(0xffffff, 1)
        this.sheenMask.drawRoundedRect(0, 0, this.w, this.h, METRICS.buttonRadius)
        this.sheenMask.endFill()
        this.sheen.mask = this.sheenMask
        this.sheen.blendMode = BLEND_MODES.ADD
        this.sheen.visible = false
        this.addChild(this.sheenMask)
        this.addChild(this.sheen)

        this.interactive = true
        this.cursor = "pointer"
        // The halo is painted past the edges; the target stays the button.
        this.hitArea = new Rectangle(0, 0, this.w, this.h)
        this.interactiveChildren = false

        this.on("pointerover", () => {
            this.hovered = true
            this.easeHover()
        })
        this.on("pointerout", () => {
            this.hovered = false
            this.pressed = false
            this.easeHover()
        })
        this.on("pointerdown", () => {
            this.pressed = true
            this.paint()
        })
        this.on("pointerup", () => {
            this.pressed = false
            this.paint()
        })

        const fire = () => {
            if (this.enabled) {
                onClick()
            }
        }
        this.on("click", fire)
        this.on("tap", fire)

        this.paint()
    }

    setEnabled = (enabled: boolean) => {
        this.enabled = enabled
        this.interactive = enabled
        this.cursor = enabled ? "pointer" : "default"
        this.paint()
    }

    setLabel = (text: string) => {
        this.label.text = text
        this.paint()
    }

    /**
     * One band of light across the face, once. Used when the card arrives, to say where the way in
     * is -- and never on a loop: a call to action that keeps flashing at a player who is still
     * deciding on a name is nagging, not inviting.
     */
    shine = (delay: number = 0) => {
        this.sheenTween?.cancel()
        this.sheenTween = tween(
            SHEEN_MS,
            t => {
                const g = this.sheen
                g.clear()
                g.visible = t > 0 && t < 1
                if (!g.visible) {
                    return
                }
                // A slanted band, travelling from fully off the left edge to fully off the right.
                const x = -SHEEN_W * 2 + (this.w + SHEEN_W * 3) * t
                const slant = this.h * 0.6
                const strength = Math.sin(Math.PI * t)
                g.beginFill(0xffffff, 0.16 * strength)
                g.moveTo(x, this.h)
                g.lineTo(x + slant, 0)
                g.lineTo(x + slant + SHEEN_W, 0)
                g.lineTo(x + SHEEN_W, this.h)
                g.closePath()
                g.endFill()
                g.beginFill(0xffffff, 0.22 * strength)
                g.moveTo(x + SHEEN_W * 0.35, this.h)
                g.lineTo(x + slant + SHEEN_W * 0.35, 0)
                g.lineTo(x + slant + SHEEN_W * 0.6, 0)
                g.lineTo(x + SHEEN_W * 0.6, this.h)
                g.closePath()
                g.endFill()
            },
            { delay, ease: t => t },
        )
    }

    dispose = () => {
        this.hoverTween?.cancel()
        this.sheenTween?.cancel()
    }

    private easeHover = () => {
        this.hoverTween?.cancel()
        const from = this.hover
        const to = this.hovered ? 1 : 0
        this.hoverTween = tween(HOVER_MS * Math.abs(to - from), t => {
            this.hover = from + (to - from) * t
            this.paint()
        })
    }

    private paint = () => {
        this.clear()

        const dim = !this.enabled
        const fillAlpha = dim ? 0.4 : 1
        const h = dim ? 0 : this.hover
        const r = METRICS.buttonRadius

        if (this.variant === "primary") {
            // The halo: two widening, fading copies of the shape, painted first so the fill sits on
            // them -- a glow without a filter.
            if (h > 0) {
                this.beginFill(COLORS.accent, 0.1 * h)
                this.drawRoundedRect(-7, -6, this.w + 14, this.h + 12, r + 7)
                this.endFill()
                this.beginFill(COLORS.accentHi, 0.14 * h)
                this.drawRoundedRect(-3, -2, this.w + 6, this.h + 4, r + 3)
                this.endFill()
            }

            const fill = this.pressed ? COLORS.accentDim : lerpColor(COLORS.accent, COLORS.accentHi, h * 0.75)
            this.beginFill(fill, fillAlpha)
            this.drawRoundedRect(0, 0, this.w, this.h, r)
            this.endFill()
            if (!this.pressed) {
                // Light along the top edge and a darker lip along the bottom: enough bevel to read
                // as a thing to press, not a coloured rectangle -- and no hard-edged gloss band.
                this.beginFill(0xffffff, 0.32 * fillAlpha)
                this.drawRect(r, 1, this.w - r * 2, 1)
                this.endFill()
                this.beginFill(0xffffff, 0.05 * fillAlpha)
                this.drawRoundedRect(1, 1, this.w - 2, this.h * 0.5, r - 1)
                this.endFill()
                this.beginFill(0x000000, 0.16 * fillAlpha)
                this.drawRect(r, this.h - 2, this.w - r * 2, 2)
                this.endFill()
            }
        } else {
            this.lineStyle({ width: 1, color: lerpColor(COLORS.panelRim, COLORS.accent, h), alpha: dim ? 0.3 : 0.55 + 0.35 * h })
            this.beginFill(COLORS.highlight, this.pressed ? 0.1 : 0.035 + 0.05 * h)
            this.drawRoundedRect(0, 0, this.w, this.h, r)
            this.endFill()
            this.lineStyle(0)
        }

        this.label.alpha = dim ? 0.55 : 1
        this.label.x = Math.round((this.w - this.label.width) / 2)
        this.label.y = Math.round((this.h - this.label.height) / 2) + (this.pressed ? 1 : 0)
    }
}
